import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  createUserRSO,
  getRSOById,
  getRSOs,
  getSpaceOrganisation,
  markRSOAsDeleted,
  updateObjectCospar,
  updateObjectName,
  updateTLE,
} from "./api"
import { allowUpdate } from "../../app/store"
import { generatePasses } from "../passes/api"
import { fetchPriorityListById } from "../priorities/slice"

const initialState = {
  byId: {}, // A map of RSO's by id
  spaceOrganisations: {
    _loadStatus: "idle", // Information about the loading status of a call
    _errorMessage: undefined,
    byCode: {},
  },
  objectTypes: ["unknown", "debris", "rocket body", "payload"], // Default types TODO pull from backend
  matchesByCtx: {
    // root context powering the observations list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // observations by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "collection/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  creating: {}, // space for new RSOs that are in the process of being created by a user
  updating: {}, // space for new RSOs that are in the process of being updated by a user
  deleting: {}, // RSOS that will be deleted on submission
}

/**
 * Thunk to perform async logic to fetch RSO's
 * matching filters.
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchRSOs = createAsyncThunk(
  "rsos/fetch",
  async ({ filter, page, context }) => {
    const response = await getRSOs({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch space organisations
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchSpaceOrgs = createAsyncThunk(
  "rsos/space-orgs/fetch",
  async () => {
    return await getSpaceOrganisation()
  }
)

/**
 * Thunk to perform async logic to fetch an RSO by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchRSOById = createAsyncThunk("rso/fetch", async (noradId) => {
  return await getRSOById(noradId)
})

/**
 * Thunk to perform async logic to create a user RSO
 * @type {AsyncThunk<*, void, {}>}
 */
export const addUserRSO = createAsyncThunk(
  "rsos/create",
  async (req = {}, { rejectWithValue }) => {
    try {
      let tleLines = req?.tle?.trim().split("\n")

      if (tleLines.length == 2) {
        tleLines = ["", ...tleLines]
      }

      let rso = await createUserRSO({
        tle_line0: tleLines[0],
        tle_line1: tleLines[1],
        tle_line2: tleLines[2],
        object_type: req?.object_type,
        object_id: req?.object_id,
        label: req?.name,
      })

      // also generate passes - we wait for a response
      // but don't report error(s) as it's a background
      // step.
      try {
        await generatePasses({
          norad_ids: [rso.id],
          from: "now",
          to: "p7d",
        })
      } catch (failed) {
        console.warn("Passes generation for", req?.object_id, "failed:", failed)
      }
      return rso
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to update the TLE a user RSO
 * NOTE: this will also trigger a passes generation
 *
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserRSOTLE = createAsyncThunk(
  "rsos/update/tle",
  async (req = {}, { rejectWithValue }) => {
    try {
      let tleLines = req?.tle?.trim().split("\n")

      if (tleLines.length === 2) {
        tleLines = ["", ...tleLines]
      }

      let rso = await updateTLE({
        id: req.id,
        tle_line0: tleLines[0],
        tle_line1: tleLines[1],
        tle_line2: tleLines[2],
        _etag: req._etag,
      })

      // also generate passes - we wait for a response
      // but don't report error(s) as it's a background
      // step.
      try {
        await generatePasses({
          norad_ids: [rso.id],
          from: "now",
          to: "p7d",
        })
      } catch (failed) {
        console.warn("Passes generation for", req?.object_id, "failed:", failed)
      }
      return rso
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to update the Name a user RSO
 *
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserRSOName = createAsyncThunk(
  "rsos/update/name",
  async (req = {}, { rejectWithValue }) => {
    try {
      return await updateObjectName(req)
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to update the Name a user RSO
 *
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateUserRSOCospar = createAsyncThunk(
  "rsos/update/cospar",
  async (req = {}, { rejectWithValue }) => {
    try {
      return await updateObjectCospar(req)
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Thunk to perform async logic to delete priority lists by their IDs
 * @type {AsyncThunk<*, void, {}>}
 */
export const markRSOAsDeletedByIds = createAsyncThunk(
  "rsos/remove",
  async (id, { rejectWithValue }) => {
    try {
      return await markRSOAsDeleted(id)
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

export const rsoSlice = createSlice({
  name: "rsos",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchRSOs.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchRSOs.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        const requestedAt = action?.meta?.arg?.requestedAt
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (
            allowUpdate(state.matchesByCtx[context].requestedAt, requestedAt)
          ) {
            // Don't reset if we are paging?
            if (action.payload.page && action.payload.page.start_index == 0) {
              state.matchesByCtx[context].ids = []
            }
            action.payload.data &&
              action.payload.data.map((rso) => {
                // Add the observations to state by id (this can include ids not retrieved in this call)
                state.byId[rso.id] = rso
                state.byId[rso.id]._at = action.payload.at
                state.matchesByCtx[context].ids.push(rso.id)
                state.matchesByCtx[context].page = action.payload.page
                state.matchesByCtx[context].at = action.payload.at
                state.matchesByCtx[context].requestedAt = requestedAt
              })
          }
        }
      })
      .addCase(fetchRSOs.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchRSOById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchRSOById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = {
              ...action.payload,
              _at: action.payload._at,
            }
          }

          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchRSOById.rejected, (state, action) => {
        // TODO: If failed because of 404 not found, store
        // in state that it's known to be not found so that components
        // don't attempt to refetch. e.g. something like
        // { id: "", _loadStatus = "notfound"}
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(fetchSpaceOrgs.pending, (state) => {
        state.spaceOrganisations._loadStatus = "loading"
      })
      .addCase(fetchSpaceOrgs.fulfilled, (state, action) => {
        state.spaceOrganisations._loadStatus = "idle"
        action.payload.map((org) => {
          state.spaceOrganisations.byCode[org.code] = org
        })
      })
      .addCase(fetchSpaceOrgs.rejected, (state, action) => {
        state.spaceOrganisations._loadStatus = "rejected"
        state.spaceOrganisations._errorMessage += action.payload
      })
      .addCase(addUserRSO.pending, (state) => {
        if (!state["creating"]) {
          state["creating"] = {}
        }
        state["creating"]._saveStatus = "creating"
      })
      .addCase(addUserRSO.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          // add the new RSO to the main state and search results
          state.byId[action.payload.id] = action.payload
          state.matchesByCtx.root.ids.push(action.payload.id)
        }
        state["creating"]._saveStatus = "idle"
      })
      .addCase(addUserRSO.rejected, (state, action) => {
        state["creating"]._saveStatus = "rejected"
        state["creating"]._errorMessage = action.payload?.error
      })
      .addCase(markRSOAsDeletedByIds.pending, (state) => {
        if (!state["deleting"]) {
          state["deleting"] = {}
        }
        state["deleting"]._saveStatus = "deleting"
      })
      .addCase(markRSOAsDeletedByIds.fulfilled, (state, action) => {
        if (action?.payload?.ids) {
          action.payload.ids?.map(
            (id) => (state.byId[id] = { id, deleted: true })
          )
        }
        state.byId[action.meta.arg].deleted = true
        state["deleting"]._saveStatus = "success"
      })
      .addCase(markRSOAsDeletedByIds.rejected, (state, action) => {
        state["deleting"]._saveStatus = "rejected"
        state["deleting"]._errorMessage += action.payload
      })
      // If we happened to fetch a priority list *including*
      // full RSO data, we listen to that here in order to update
      // RSO state.
      .addCase(fetchPriorityListById.fulfilled, (state, action) => {
        if (Array.isArray(action?.payload?.rsos)) {
          action.payload.rsos.forEach((rso) => (state.byId[rso.id] = rso))
        }
      })
      .addCase(updateUserRSOTLE.pending, (state) => {
        if (!state["updating"]) {
          state["updating"] = {}
        }
        state["updating"]._saveStatus = "updating"
      })
      .addCase(updateUserRSOTLE.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
        }
        state["updating"]._saveStatus = "idle"
      })
      .addCase(updateUserRSOTLE.rejected, (state, action) => {
        state["updating"]._saveStatus = "rejected"
        state["updating"]._errorMessage = action.payload?.error
      })
      .addCase(updateUserRSOName.pending, (state) => {
        if (!state["updating"]) {
          state["updating"] = {}
        }
        state["updating"]._saveStatus = "updating"
      })
      .addCase(updateUserRSOName.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id] = action.payload
        }
        state["updating"]._saveStatus = "idle"
      })
      .addCase(updateUserRSOName.rejected, (state, action) => {
        state["updating"]._saveStatus = "rejected"
        state["updating"]._errorMessage = action.payload?.error
      })
  },
})

// Selector for all schedules
export const selectRSOTypes = (state) => state.rsos.objectTypes
export const selectSpaceOrgsByCode = (state) =>
  state.rsos.spaceOrganisations.byCode
export const selectSpaceOrgs = (state) =>
  Object.values(state.rsos.spaceOrganisations.byCode)
export const selectRSOsById = (state) => state.rsos.byId

// selector for the user RSO creation process
export const selectCreatingUserRSO = (state) => state.rsos.creating

// selector for the user RSO deletion process
export const selectDeletingUserRSO = (state) => state.rsos.deleting

// selector for the user RSO update process
export const selectUpdatingUserRSO = (state) => state.rsos.updating

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectRSOsByContext(state, `collection/${id}`))
 */
export const selectRSOsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.rsos.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`observations, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectRSOsByIds(state, ["1", "2"]))
 */
export const selectRSOsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectRSOsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const { clearMatches } = rsoSlice.actions

export default rsoSlice.reducer
